import { AssetsService } from '../../../../@core/utils/assets.service';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges,
} from '@angular/core';
import { Router } from '@angular/router';
import { marker as _ } from '@colsen1991/ngx-translate-extract-marker';
import { NbDialogService, NbCardModule, NbAlertModule, NbButtonModule } from '@nebular/theme';
import { TranslateService, TranslateModule } from '@ngx-translate/core';
import * as Highcharts from 'highcharts';
import { combineLatest, Subscription } from 'rxjs';
import { first } from 'rxjs/operators';

import { KPI } from '../../../../@core/api';
import {
  I18nService,
  LayoutService,
  ScopeService,
  StateLevelsService,
} from '../../../../@core/utils';
import { BaseChartComponent } from '../base-chart/base-chart.component';
import { DatePipe, LowerCasePipe, NgClass } from '@angular/common';
import { HighchartsChartModule } from 'highcharts-angular';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { BasicDialogComponent } from '../../basic-dialog/basic-dialog.component';
import { IdMap } from '../../../../@core/utils/scope.service';

@Component({
    selector: 'app-lifecycle-chart',
    styleUrls: ['./lifecycle-chart.component.scss'],
    templateUrl: './lifecycle-chart.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: true,
    imports: [
    NbCardModule,
    NgClass,
    
    FontAwesomeModule,
    HighchartsChartModule,
    NbAlertModule,
    NbButtonModule,
    TranslateModule
],
})
export class LifecycleChartComponent extends BaseChartComponent implements OnInit, OnChanges, OnDestroy {

  @Input() public asset: KPI.MappedAsset;

  private title: string = "";
  private color: string;
  private subscriptions: Subscription[] = [];
  private levelsMap: { [tick: number]: { name: string } } = {};
  private lifecycle: KPI.AssetLifecycle;
  private investmentTypes: IdMap<KPI.InvestmentType>;
  public hasNotation: boolean;

  constructor(
    protected dialogService: NbDialogService,
    protected layoutService: LayoutService,
    protected router: Router,
    private stateLevelsService: StateLevelsService,
    private assetsService: AssetsService,
    private translate: TranslateService,
    private i18nService: I18nService,
    private cdRef: ChangeDetectorRef,
    private scope: ScopeService,
  ) {
    super(dialogService, layoutService, router);
  }

  public ngOnInit(): void {
    super.ngOnInit();
    this.subscriptions.push(
      this.stateLevelsService.levelsNames$.subscribe((levelsNames) => {
        this.levelsMap = {
          0: { name: this.stateLevelsService.getStateLabel('out_of_service') }, // Rebut
          22: { name: levelsNames[0] }, // Critical
          46: { name: levelsNames[1] }, // Vieillissant
          70: { name: levelsNames[2] }, // Acceptable
          94: { name: levelsNames[3] }, // Bon
          100: { name: this.stateLevelsService.getStateLabel('new') }, // Neuf
        };
      }),
    );
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes.asset) {
      if (this.asset) {
        this.update();
      } else {
        this.loading = false;
        this.cdRef.detectChanges();
      }
    }
  }

  public ngOnDestroy(): void {
    super.ngOnDestroy();
    this.subscriptions.forEach((subscription) => subscription.unsubscribe());
  }

  protected update(): void {
    this.subscriptions.push(
      combineLatest([
        this.assetsService.getAssetLifecycle(this.asset.id),
        this.scope.investmentTypes$
      ]).pipe(first()).subscribe(([lifecycle, investmentTypes]) => {
        this.hasNotation = !!this.asset.asset_notations.length
        this.lifecycle = lifecycle;
        this.investmentTypes = investmentTypes;
        const lastAuditData = [lifecycle.audit_points.pop()];
        const consistency = lifecycle.consistency_check;
        const showConsistency: boolean = !consistency.is_consistent
          && !lifecycle.is_replacement_planned;

        const willBeReplaced: boolean | null = !!lifecycle?.next_asset_curve.length

        // Title if the asset is a replacement of another asset
        const futureAssetText: string = this.translate.instant('Future asset');
        if (this.asset.step === KPI.ASSET_STEP.ACTIVE) {
          this.title = "";
          this.color = undefined;
        } else {
          if (lifecycle.is_planned_replacement_of) {
            this.title = futureAssetText + ' (' + this.translate.instant('planned invest') + ')';
            this.color = "#548235";
          } else if (lifecycle.is_hypothesis_replacement_of) {
            this.title = futureAssetText + ' (' + this.translate.instant('unplanned invest') + ')';
            this.color = "#c55a11";
          } else {
            this.title = "";
            this.color = undefined;
          }
        }

        // Get half of the historic + expected asset curve X axis
        const halfXAxis = Math.round(
          (
            (lifecycle.historic_asset_curve?.length || 0)
            + (lifecycle.expected_asset_curve?.length || 0)
          ) / 2,
        );

        // SERIES
        const series = [
          
          // HISTORIC ASSET CURVE
          {
            id: 'history',
            type: 'line',
            data: lifecycle.historic_asset_curve,
            name: this.translate.instant(
              'pages.common.charts.lifecycle-chart.history',
            ),
            color: this.color || '#41596b',
            marker: { symbol: 'circle' },
            tooltip: this.getSerieTooltipOptions(),
            showInLegend: false,
            visible: this.hasNotation
          },
          // EXPECTED ASSET CURVE
          {
            id: 'projected',
            type: 'line',
            dashStyle: 'Dash',
            data: lifecycle.expected_asset_curve,
            name: this.translate.instant(
              'pages.common.charts.lifecycle-chart.projection',
            ),
            color: this.color || '#41596b',
            marker: { symbol: 'diamond' },
            tooltip: this.getSerieTooltipOptions(),
            showInLegend: false,
            visible: this.hasNotation
          },
          // OTHER AUDIT POINTS
          {
            ...this.auditSerieTemplate(false),
            name: this.translate.instant(
              'pages.common.charts.lifecycle-chart.previous_audits',
              { nbAudit: lifecycle.audit_points.length }
            ),
            data: lifecycle.audit_points,
            showInLegend: false,
            visible: this.hasNotation
          },
          // LAST AUDIT POINT
          {
            ...this.auditSerieTemplate(),
            name: this.translate.instant(
              'pages.common.charts.lifecycle-chart.last_audit',
            ),
            data: lastAuditData,
            color: '#ff9696',
            showInLegend: true,
            visible: this.hasNotation
          },
          // HYPOTHESIS INVESTMENTS
          {
            ...this.investSerieTemplate(false),
            zIndex: 5,
            type: 'scatter',
            name: this.translate.instant(
              'pages.common.charts.lifecycle-chart.investments-hypothesis',
            ),
            data: lifecycle.hypothesis_investments_points,
            showInLegend: (lifecycle.hypothesis_investments_points?.length && willBeReplaced && this.hasNotation),
          },
          // PLANNED INVESTMENTS
          {
            ...this.investSerieTemplate(),
            zIndex: 6,
            type: 'scatter',
            name: this.asset.investments.length === 1 ? this.asset.investments[0].label : this.translate.instant(
              'pages.common.charts.lifecycle-chart.investments-deviation',
            ),
            data: lifecycle.planned_investments_points,
            showInLegend: lifecycle.planned_investments_points?.length && willBeReplaced,
            visible: this.hasNotation
          },
          // MIN SUGGESTED LIEFETIME CURVE
          {
            ...this.suggestedCurveTemplate(),
            id: 'suggested',
            data: consistency.min_suggested_curve,
            visible: showConsistency && this.hasNotation,
            showInLegend: false,
          },
          // MAX SUGGESTED LIEFETIME CURVE
          {
            ...this.suggestedCurveTemplate(),
            linkedTo: 'suggested',
            data: consistency.max_suggested_curve,
            visible: showConsistency && this.hasNotation,
            showInLegend: false,
          },
          // NEXT ASSET (FUTURE EQUIPMENT)
          {
            xAxis: 'main',
            id: 'future',
            type: 'line',
            gapSize: 1,
            // Limit the next asset curve to 1/2 of the current asset life
            data: lifecycle.next_asset_curve?.slice(
              0,
              halfXAxis,
            ) || [],
            dashStyle: lifecycle.is_replacement_planned ? 'Solid' : 'Dash',
            name: this.translate.instant('Future equipment'),
            color: '#dfc77e',
            tooltip: this.getSerieTooltipOptions(),
            visible: lifecycle.next_asset_curve?.length && willBeReplaced && this.hasNotation,
            showInLegend: false,
            // @ts-ignore
            isNextAssetSerie: true,
          },
          // SUGGESTED LIEFETIME GREEN AREA
          {
            linkedTo: 'suggested',
            type: 'area',
            zIndex: -1,
            color: 'rgba(176, 204, 168, .5)',
            fillColor: 'rgba(176, 204, 168, .5)',
            enableMouseTracking: false,
            lineWidth: 0,
            showInLegend: false,
            marker: {
              enabled: false,
            },
            data: [
              {
                x: consistency.min_suggested_lifetime,
                y: 100,
              },
              {
                x: consistency.max_suggested_lifetime,
                y: 100,
              },
            ],
            visible: showConsistency && willBeReplaced && this.hasNotation,
          },
        ]

        // OPTIONS
        this.options = {
          ...this.getOptions(),
          xAxis: [
            {
              id: 'main',
              allowDecimals: false,
              lineColor: this.color || '',
              plotLines: [{ value: 0, width: 1, color: this.color || '#cccccc', zIndex: 5 }],
              labels: {
                style: {
                  color: this.color || '',
                },
                formatter: (() => {
                  const translate = this.translate;
                  return function () {
                    return translate.instant(
                      _('pages.common.charts.lifecycle-chart.lifetime'),
                      { value: this.value },
                    );
                  };
                })(),
              },
            },
            // Top x-axis showing the year
            {
              linkedTo: 0,
              opposite: true,
              lineColor: this.color || '',
              labels: {
                style: {
                  color: this.color || '',
                  fontSize: '1em',
                },
                formatter: function () {
                  return (Number(this.value) + lifecycle.installation_year).toString();
                },
              },
            },
          ],
          // @ts-ignore
          series: series,
        };
        this.loading = false;
        this.reflow();
        this.cdRef.detectChanges();
      }),
    );
  }

  protected getOptions(): Highcharts.Options {
    return {
      ...super.getOptions(),
      title: {
        style: {
          fontSize: '1.75em',
          color: this.color || '#000',
        },
        margin: 35,
        align: 'left',
        text: this.title,
      },
      series: [],
      yAxis: {
        title: undefined,
        tickPositions: [0, 22, 46, 70, 94, 100],
        gridLineWidth: 0,
        lineWidth: 1,
        tickWidth: 0,
        minorTicks: false,
        lineColor: this.color || '',
        plotLines: [],
        labels: {
          formatter: (() => {
            const levelsMap = this.levelsMap;
            return function () {
              const level = levelsMap[this.value];
              if (level) {
                return level.name;
              }
            };
          })(),
          style: {
            color: this.color || '',
          }
        },
      },
      legend: {
        enabled: true,
        align: 'center',
        itemHiddenStyle: super.getOptions().legend.itemHiddenStyle,
        verticalAlign: 'top',
        itemStyle: {
          fontWeight: 'normal',
          fontSize: '1em'
        },
        symbolWidth: 20,
        symbolHeight: 20,
        itemMarginBottom: 10,
      },
      plotOptions: {
        line: {
          lineWidth: 1,
          marker: {
            enabled: false,
          },
        },
        series: {
          stickyTracking: false,
          point: {
            events: {
              click: (event: Highcharts.SeriesClickEventObject) => {
                const { investment } = event.point as any;
                if (investment) {
                  this.router.navigate([
                    '/insight/investments/',
                    investment.id,
                  ]);
                }
              },
            },
          },
        },
      },
    };
  }

  public getSerieTooltipOptions(): Highcharts.SeriesTooltipOptionsObject {
    const installationYear = this.asset.installation_year;
    return {
      headerFormat: '',
      pointFormatter: (() => {
        const translate = this.translate;
        const stateLevelsService = this.stateLevelsService;
        return function () {
          const isNextAssetSerie: boolean = this.series.userOptions['isNextAssetSerie'];
          const pointYear = this.x + installationYear;
          const state = stateLevelsService.getTechnicalStateLabelFromNote(
            this.y,
          );
          const calculated_note = translate.instant(
            _('pages.common.charts.lifecycle-chart.calculated_note'),
            { year: pointYear },
          );
          const roundedNote = Math.round(this.y);
          const formattedNote = translate.instant(
            _('pages.common.charts.lifecycle-chart.note'),
            {
              value: roundedNote,
              state: state,
            },
          );

          return calculated_note
            + (isNextAssetSerie
              ? ' <span style="color: #dfc77e">(' + translate.instant(_('Future equipment')) + ')</span>'
              : '')
            + '<br>'
            + formattedNote;
        };
      })(),
    };
  };

  private auditSerieTemplate(last: boolean = true) {
    const translate = this.translate;
    const stateLevelsService = this.stateLevelsService;
    const auditMarker = {
      symbol: 'url(/assets/images/audit-point.png)',
      width: 24,
      height: 24,
    };

    const lastAuditMarker = {
      ...auditMarker,
      symbol: 'url(/assets/images/last-audit-point.png)',
    };

    return {
      type: 'scatter',
      data: null,
      name: null,
      color: '#41596b',
      cursor: 'pointer',
      marker: last ? lastAuditMarker : auditMarker,
      tooltip: {
        headerFormat: '',
        pointFormatter: function () {
          const auditUser = this.options.user?.name;
          const datePipe = new DatePipe(translate.getBrowserCultureLang());
          const auditDate: string = datePipe.transform(this.options.datetime, 'd MMM y');
          const auditState: string = stateLevelsService.getTechnicalStateLabelFromNote(this.y);

          const formattedAudit = translate.instant(
            _('pages.common.charts.lifecycle-chart.audit'),
            {
              name: auditUser,
              date: auditDate,
            },
          );

          const roundedNote = Math.round(this.y);
          const formattedNote = translate.instant(
            _('pages.common.charts.lifecycle-chart.note'),
            {
              value: roundedNote,
              state: auditState,
            },
          );

          let tooltip = `${ auditUser ? formattedAudit : auditDate }<br>${formattedNote}`;
          if (this.options.rating_reasons?.length) {
            const reasonLabel = translate.instant(
              _('pages.common.charts.lifecycle-chart.reason-label'),
            );
            const ratingReasonNames = this.options.rating_reasons.map((rr) => rr.name).join(', ');

            if (ratingReasonNames?.length) {
              tooltip += `<br>${reasonLabel}: ${ratingReasonNames}`;
            }
          }

          return tooltip;
        },
      }
    }
  }

  private investSerieTemplate(planned: boolean = true) {
    const i18nService = this.i18nService;
    const lifecycle = this.lifecycle;
    const invMarker = {
      symbol: 'url(/assets/images/euro-marker-red.svg)',
      width: 20,
      height: 20,
    };

    const hypothesisInvMarker = {
      ...invMarker,
      symbol: 'url(/assets/images/euro-marker.svg)',
    };
    const investmentTypes = this.investmentTypes;
    const translate = this.translate;
    const lowerCase = new LowerCasePipe();

    return {
      color: planned ? 'b8d0e9' : '#ff1d1d',
      marker: planned ? invMarker : hypothesisInvMarker,
      cursor: 'pointer',
      tooltip: {
        headerFormat: '',
        useHtml: true,
        pointFormatter: (() => {
          return function () {
            const formattedPrice = I18nService.formatCurrencyValue(
              this.price,
              i18nService.getCurrency(),
            );
            const investmentType = investmentTypes[this.investment_type_id];
            return translate.instant(
              _('pages.common.charts.lifecycle-chart.investment_label'),
              {
                investmentType: lowerCase.transform(investmentType.name),
                year: this.x + lifecycle.installation_year
              }
            ) + ` : <b>${this.investment_label} (${formattedPrice})</b>`;
          };
        })(),
      },
    }
  }

  private suggestedCurveTemplate() {
    return {
      type: 'line',
      zIndex: 1,
      name: this.translate.instant(
        'pages.common.charts.lifecycle-chart.suggested',
      ),
      color: '#B0CCA8',
      marker: {
        symbol: 'diamond',
      },
      tooltip: this.getSerieTooltipOptions(),
    };
  }

  public displayAlert() {
    this.dialogService.open(
      BasicDialogComponent,
      {
        context: {
          title: this.translate.instant('pages.common.charts.lifecycle-chart.alert_inconsistent_dialog_title'),
          content: this.alert.message,
          canCancel: false,
        },
      },
    )
  }
}
